import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, getFirestore, updateDoc } from "firebase/firestore";
import { getDatabase, ref, onValue } from "firebase/database";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { Card, Form, Button } from "react-bootstrap";
import { app } from "../firebase";
import "./Dash.css";
import Logo from '../Images/logoblack.svg'
import bikeIcon from '../Images/bike2.png'

const containerStyle = {
  width: "100%",
  height: "300px", // smaller map on mobile
};

function TrackOrder() {
  const { orderId } = useParams();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyA297W_3urY4B_E0SbyfM_C_eqy6a6JGrY",
  });

  const [order, setOrder] = useState(null);
  const [driver, setDriver] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const db = getFirestore(app);
  const rtdb = getDatabase(app);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const orderRef = doc(db, "Orders", orderId);
        const orderSnap = await getDoc(orderRef);

        if (orderSnap.exists()) {
          const orderData = orderSnap.data();
          setOrder(orderData);

          if (orderData.clientFeedback) {
            setSubmitted(true);
          }

          if (orderData.status === "onJourney" && orderData.driverId) {
            const driverRef = doc(db, "drivers", orderData.driverId);
            const driverSnap = await getDoc(driverRef);

            if (driverSnap.exists()) {
              const driverData = driverSnap.data();
              setDriver(driverData);

              const driverUserUid = driverData.uid;
              const dbRef = ref(rtdb, `drivers/${driverUserUid}/lastLocation`);

              onValue(dbRef, (snapshot) => {
                if (snapshot.exists()) {
                  setDriverLocation(snapshot.val());
                }
              });
            }
          }
        }
      } catch (err) {
        console.error("❌ Error fetching order:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [db, rtdb, orderId]);

  const handleFeedbackSubmit = async () => {
    if (!feedback.trim()) return;
    setSubmitting(true);
    try {
      const orderRef = doc(db, "Orders", orderId);
      await updateDoc(orderRef, {
        clientFeedback: feedback.trim(),
        clientFeedbackDate: new Date(),
      });
      setSubmitted(true);
      setFeedback("");
    } catch (err) {
      console.error("❌ Error saving feedback:", err);
      alert("Error submitting feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p className="text-center">Loading order details...</p>;
  if (!order) return <p className="text-center">Order not found.</p>;

  return (
    <div className="p-3 mx-auto" style={{ maxWidth: "700px" }}>
      {/* Logo */}
      <div className="text-center mb-3">
        <img
          src={Logo} // replace with your logo path
          alt="Logo"
          style={{ width: "60px", height: "60px" }}
        />
      </div>

      <h2 className="mb-4 text-center fs-4">Track Your Order</h2>

      {/* Order Info */}
      <Card className="mb-3 p-3 shadow-sm small-text">
        <h6 className="fw-bold">Order ID: {orderId}</h6>
        <p>Status: <strong>{order.status}</strong></p>
        <p>From: {order.fromDetails} ({order.fromLocation})</p>
        <p>To: {order.toDetails} ({order.toLocation})</p>
        <p>Delivery Instructions: {order.deliveryInstructions}</p>
      </Card>

      {/* Completed */}
      {order.status === "Completed" && (
        <Card className="p-3 shadow-sm small-text">
          <h6>✅ This order has been completed.</h6>
          {submitted ? (
            <div className="mt-3 text-center">
              <p className="text-success fw-bold">✅ Thank you for your feedback!</p>
            </div>
          ) : (
            <Form className="mt-3">
              <Form.Group>
                <Form.Label className="small-text">
                  Leave your feedback about this order
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Type your feedback here..."
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  className="small-text"
                />
              </Form.Group>
              <Button
                className="mt-2 w-100"
                variant="primary"
                onClick={handleFeedbackSubmit}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Feedback"}
              </Button>
            </Form>
          )}
        </Card>
      )}

      {/* Pending */}
      {order.status === "Pending" && (
        <Card className="p-3 text-center bg-light shadow-sm small-text">
          <h6>⌛ This order is pending. Tracking will be available once it starts.</h6>
        </Card>
      )}

      {/* On Journey (Driver Tracking) */}
      {order.status === "onJourney" && driver && (
        <Card className="p-3 shadow-sm small-text">
          <h6>Driver Information</h6>
          <div className="d-flex align-items-center mb-2">
            <img
              src={driver.profileImage}
              alt="Driver"
              className="rounded-circle me-2"
              style={{ width: "50px", height: "50px", objectFit: "cover" }}
            />
            <div>
              <p className="mb-1 fw-bold">{driver.full_name}</p>
              <p className="mb-1">Phone: {driver.userphone}</p>
            </div>
          </div>

          {isLoaded && driverLocation ? (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={{ lat: driverLocation.lat, lng: driverLocation.lng }}
              zoom={14}
            >


              <Marker
                position={{ lat: driverLocation.lat, lng: driverLocation.lng }}
                icon={{
                  url: bikeIcon, // place your bike image in the public folder
                  scaledSize: new window.google.maps.Size(60, 60), // adjust size
                  origin: new window.google.maps.Point(0, 0),
                  anchor: new window.google.maps.Point(20, 20), // centers the icon
                }}
              />
            </GoogleMap>
          ) : (
            <p className="text-muted">Fetching driver location...</p>
          )}
        </Card>
      )}

      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 576px) {
          .small-text {
            font-size: 0.9rem;
          }
          h2 {
            font-size: 1.25rem !important;
          }
          h5, h6 {
            font-size: 1rem !important;
          }
          p {
            margin-bottom: 0.4rem;
          }
        }
      `}</style>
    </div>
  );
}

export default TrackOrder;